import React, { useCallback, useEffect } from "react";
import {
  PreloadedQuery,
  fetchQuery,
  graphql,
  useMutation,
  usePreloadedQuery,
  useQueryLoader,
  useRelayEnvironment,
} from "react-relay";

import { WidgetScreen } from "../../common/WidgetScreen";
import { WASizePreview } from "../../WidgetAdd/WASizePreview/WASizePreview";
import { overviewWidgetSizes } from "../../WidgetAdd/utils/widgetSizes/overviewWidgetSizes";
import { WAWidgetSizeToOverviewSize } from "../../WidgetAdd/utils/WAWidgetSizeToOverviewSize";

import { useDebouncedCallback } from "use-debounce";
import { WidgetSizeT } from "../../localTypes/WidgetSizeT";

import {
  WAFileAddMockPreviewQuery,
  WAFileAddMockPreviewQuery$variables,
} from "./__generated__/WAFileAddMockPreviewQuery.graphql";
import { WAFileMutation } from "./__generated__/WAFileMutation.graphql";
import { WAFileNavigationProp, WAFileRouteProp } from "./WAFileNavigationProp";
import { WAFileSettings } from "./WAFileSettings";
import { WidgetFile } from "widgets/src/WidgetFile/WidgetFile";

type Props = {
  navigation: WAFileNavigationProp<"WAFileMain">;
  route: WAFileRouteProp<"WAFileMain">;
};

const WAFILE_PREVIEW_QUERY = graphql`
  query WAFileAddMockPreviewQuery(
    $input: PreviewWidgetFileInput!
    $storecastId: ID!
  ) @raw_response_type {
    dashboard(id: $storecastId) @required(action: THROW) {
      previewWidgetFile(input: $input) {
        ...WidgetFile_widgetFile
      }
    }
  }
`;

function WAFilePreview(props: {
  query: PreloadedQuery<WAFileAddMockPreviewQuery>;
}) {
  const { dashboard } = usePreloadedQuery<WAFileAddMockPreviewQuery>(
    WAFILE_PREVIEW_QUERY,
    props.query
  );

  return (
    <WidgetFile
      widget={dashboard?.previewWidgetFile}
      idiom="TOUCH"
      mode="IDLE"
    />
  );
}

export function WAFileMainComponent(props: {
  slideshowDuration: number;
  fileIds: string[];
  maxSize: WidgetSizeT;
  unitSize: number;
  onCreate(size: WidgetSizeT): void;
  loading: boolean;
  onAddFile(): void;
  setSlideShowDuration(duration: number): void;
  onRemoveFile(fileId: string): void;
  onSetOrder(order: string[]): void;
  preview?: React.ReactNode;
}) {
  return (
    <WidgetScreen
      settings={
        <>
          <WAFileSettings
            slideshowDuration={props.slideshowDuration}
            setSlideShowDuration={props.setSlideShowDuration}
            onRemoveFile={props.onRemoveFile}
            onAddFile={props.onAddFile}
            fileIds={props.fileIds}
            onSetOrder={props.onSetOrder}
          />
        </>
      }
      preview={
        <WASizePreview
          loading={props.loading}
          onCreate={props.onCreate}
          unitSize={props.unitSize}
          maxSize={props.maxSize}
          widgetSizes={overviewWidgetSizes}
          disabled={props.fileIds.length === 0}
        >
          {props.preview}
        </WASizePreview>
      }
    />
  );
}

export function WAFileMain(props: Props) {
  const [createWidgetFileMutation, loading] = useMutation<WAFileMutation>(
    graphql`
      mutation WAFileMutation(
        $storecastId: ID!
        $input: CreateWidgetFileInput!
      ) {
        dashboard(dashboardId: $storecastId) {
          createWidgetFile(widget: $input) {
            id
          }
        }
      }
    `
  );

  const onCreate = useCallback(
    (size: WidgetSizeT) => {
      if (props.route.params.fileIds) {
        createWidgetFileMutation({
          variables: {
            storecastId: props.route.params.storecastId,
            input: {
              positionX:
                props.route.params.position.x +
                props.route.params.position.c * 2,
              positionY: props.route.params.position.y,
              size: WAWidgetSizeToOverviewSize(size),
              slideshowDuration: props.route.params.slideshowDuration,
              files: props.route.params.fileIds.map((fileId) => ({
                fileId,
              })),
            },
          },
          onCompleted: () => {
            props.navigation.pop(2);
          },
        });
      }
    },
    [
      props.route.params.fileIds,
      props.route.params.storecastId,
      props.route.params.position.x,
      props.route.params.position.c,
      props.route.params.position.y,
      props.route.params.slideshowDuration,
      props.navigation,
      createWidgetFileMutation,
    ]
  );

  const [previewQuery, loadPreview] =
    useQueryLoader<WAFileAddMockPreviewQuery>(WAFILE_PREVIEW_QUERY);

  const environment = useRelayEnvironment();

  const loadPreviewQuery = useDebouncedCallback(async (fileIds: string[]) => {
    try {
      const variables: WAFileAddMockPreviewQuery$variables = {
        storecastId: props.route.params.storecastId,
        input: {
          size: "TWO_X_FOUR",
          files: fileIds.map((fileId) => ({ fileId })),
        },
      };
      await fetchQuery(
        environment,
        WAFILE_PREVIEW_QUERY,
        variables
      ).toPromise();
      loadPreview(variables);
    } catch {}
  }, 300);

  useEffect(() => {
    loadPreviewQuery(props.route.params.fileIds);
  }, [loadPreviewQuery, props.route.params.fileIds]);

  return (
    <WAFileMainComponent
      fileIds={props.route.params.fileIds}
      slideshowDuration={props.route.params.slideshowDuration}
      maxSize={props.route.params.maxSize}
      unitSize={props.route.params.unitSize}
      onCreate={onCreate}
      loading={loading}
      onAddFile={() => {
        props.navigation.navigateDeprecated("WAFileSelect", {
          ...props.route.params,
        });
      }}
      setSlideShowDuration={(duration) => {
        props.navigation.setParams({
          slideshowDuration: duration,
        });
      }}
      onRemoveFile={(removeId) => {
        props.navigation.setParams({
          fileIds: props.route.params.fileIds.filter((f) => f !== removeId),
        });
      }}
      onSetOrder={(order) => {
        props.navigation.setParams({
          fileIds: order,
        });
      }}
      preview={
        previewQuery ? <WAFilePreview query={previewQuery} /> : undefined
      }
    />
  );
}
